import { useQueries } from '@tanstack/react-query';
import {
  familyKeys,
  tasksControllerDayV1,
  tasksControllerWeekV1,
  type DayTasksResponse,
  type OccurrenceResponse,
  type WeekAgendaResponse,
} from '@kidshome/api-contract';

/**
 * Fan out the per-child day read (`GET /v1/tasks/day?assigneeId&date`) across a household's children
 * for one local date. A GUARDIAN has no chores of their own, so the household-wide "today" picture is
 * the union of each kid's day. `useQueries` keeps the SAME query key + fn as the `useDayTasks` hook
 * (`familyKeys.day`), so a kid card's own `useDayTasks(member.id, today)` shares this cache — no
 * duplicate fetches. Disabled until the caller has the child ids.
 */
export interface HouseholdDayTasks {
  readonly isPending: boolean;
  readonly isError: boolean;
  /** Every occurrence across all children for the date (flattened from each child's groups). */
  readonly occurrences: readonly OccurrenceResponse[];
  readonly refetch: () => void;
}

function flattenDay(day: DayTasksResponse | undefined): OccurrenceResponse[] {
  if (!day) return [];
  return day.groups.flatMap((g) => g.occurrences);
}

export function useHouseholdDayTasks(
  childIds: readonly string[],
  date: string,
  enabled = true,
): HouseholdDayTasks {
  const results = useQueries({
    queries: childIds.map((childId) => ({
      queryKey: familyKeys.day(childId, date),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        tasksControllerDayV1({ assigneeId: childId, date }, undefined, signal),
      enabled: enabled && !!childId && !!date,
    })),
  });

  return {
    // Pending only while we actually have children to fetch (empty → resolved-empty, not "loading").
    isPending: childIds.length > 0 && results.some((r) => r.isPending),
    isError: results.some((r) => r.isError),
    occurrences: results.flatMap((r) => flattenDay(r.data)),
    refetch: () => results.forEach((r) => void r.refetch()),
  };
}

/** An occurrence tagged with which child it belongs to (a guardian's merged agenda needs the owner). */
export interface OwnedOccurrence {
  readonly occurrence: OccurrenceResponse;
  readonly childId: string;
}

/** A merged household day in the week agenda: the local date + every child's occurrences for it. */
export interface HouseholdAgendaDay {
  readonly localDate: string;
  readonly items: readonly OwnedOccurrence[];
}

export interface HouseholdWeekAgenda {
  readonly isPending: boolean;
  readonly isError: boolean;
  /** The 7-day window, each day merging all children's occurrences (sorted by local date ascending). */
  readonly days: readonly HouseholdAgendaDay[];
  readonly refetch: () => void;
}

/**
 * Fan out the per-child week agenda (`GET /v1/tasks/week?assigneeId&weekStart`) across a household's
 * children and MERGE by local day. A guardian has no chores of their own, so their "family agenda" is
 * the union of each kid's week — each occurrence kept tagged with its `childId` so the UI can label
 * who it belongs to. Reuses the SAME query key/fn as `useWeekAgenda` so a child's own agenda shares
 * the cache.
 */
export function useHouseholdWeekAgenda(
  childIds: readonly string[],
  weekStart: string,
  enabled = true,
): HouseholdWeekAgenda {
  const results = useQueries({
    queries: childIds.map((childId) => ({
      queryKey: familyKeys.week(childId, weekStart),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        tasksControllerWeekV1({ assigneeId: childId, weekStart }, undefined, signal),
      enabled: enabled && !!childId && !!weekStart,
    })),
  });

  // Merge every child's days into one date→items map.
  const byDate = new Map<string, OwnedOccurrence[]>();
  results.forEach((r, index) => {
    const childId = childIds[index] ?? '';
    const agenda = r.data as WeekAgendaResponse | undefined;
    if (!agenda) return;
    for (const day of agenda.days) {
      const bucket = byDate.get(day.localDate) ?? [];
      for (const occurrence of day.occurrences) bucket.push({ occurrence, childId });
      byDate.set(day.localDate, bucket);
    }
  });

  const days: HouseholdAgendaDay[] = [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([localDate, items]) => ({ localDate, items }));

  return {
    isPending: childIds.length > 0 && results.some((r) => r.isPending),
    isError: results.some((r) => r.isError),
    days,
    refetch: () => results.forEach((r) => void r.refetch()),
  };
}
